import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Star, X } from 'lucide-react';
import type { ArchivoDto } from '@/modelos/Archivo';
import { archivosService } from '@/services/api/archivos.service';

type LocalImageItem = {
  id: string;
  url: string;
  file?: File;
  isExisting: boolean;
  esPrincipal: boolean;
};

const MAX_IMAGES = 5;
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const toLocalId = (file: File) => `${file.name}-${file.lastModified}-${file.size}`;

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });

const resolveArchivoItems = async (archivos: ArchivoDto[]): Promise<LocalImageItem[]> => {
  const sorted = [...archivos].sort((a, b) => {
    if (a.esPrincipal && !b.esPrincipal) return -1;
    if (!a.esPrincipal && b.esPrincipal) return 1;
    return a.orden - b.orden;
  });

  return Promise.all(
    sorted.map(async (a) => {
      let url = a.urlPublica || '';
      if (!url) {
        try {
          const res = await archivosService.obtenerUrlTemporal(a.id);
          url = res.data?.url || '';
        } catch {
          url = '';
        }
      }
      return { id: a.id, url, isExisting: true, esPrincipal: a.esPrincipal };
    }),
  );
};

interface ProductImageUploadProps {
  imagePreview?: string;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isFieldVisible: (fieldId: string) => boolean;
  isFieldRequired: (fieldId: string) => boolean;
  showCheck?: boolean;
  renderCheck?: (className?: string) => React.ReactNode;
  onFilesChange?: (files: File[]) => void;
  archivosExistentes?: ArchivoDto[];
  productoId?: string;
}

export const ProductImageUpload: React.FC<ProductImageUploadProps> = ({
  onUpload,
  isFieldVisible,
  isFieldRequired,
  showCheck,
  renderCheck,
  onFilesChange,
  archivosExistentes = [],
  productoId,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const prevArchivosRef = useRef<string>('');
  const [images, setImages] = useState<LocalImageItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  const isVisible = isFieldVisible('imagen');
  const activeImage = images[activeIndex];
  const canAddMore = images.length < MAX_IMAGES;

  useEffect(() => {
    const currentIds = archivosExistentes.map(a => a.id).sort().join(',');
    if (currentIds === prevArchivosRef.current) return;
    prevArchivosRef.current = currentIds;

    if (archivosExistentes.length > 0) {
      void resolveArchivoItems(archivosExistentes).then(items => {
        setImages(items);
        setActiveIndex(0);
      });
    } else {
      setImages(prev => (prev.every(img => img.isExisting) ? [] : prev));
    }
  }, [archivosExistentes]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 2800);
    return () => window.clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    if (!onFilesChange || productoId) return;
    onFilesChange(images.flatMap(img => (img.file ? [img.file] : [])));
  }, [images, onFilesChange, productoId]);

  const setMainImageInForm = (file?: File) => {
    onUpload({ target: { files: file ? [file] : [] } } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const refetchFromApi = useCallback(async () => {
    if (!productoId) return;
    const res = await archivosService.obtenerDeProducto(productoId);
    if (res.ok && res.data) {
      const imgs = res.data.filter(a => a.mimeType.startsWith('image/'));
      prevArchivosRef.current = imgs.map(a => a.id).sort().join(',');
      const items = await resolveArchivoItems(imgs);
      setImages(items);
      setActiveIndex(0);
    }
  }, [productoId]);

  const handleAddImages = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isVisible) return;
    const selected = Array.from(event.target.files ?? []);
    if (inputRef.current) inputRef.current.value = '';
    if (selected.length === 0) return;

    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      setNotice(`Máx. ${MAX_IMAGES} imágenes`);
      return;
    }

    const candidates = selected.slice(0, remainingSlots);
    let skipped = selected.length - candidates.length;
    const validFiles: File[] = [];

    for (const file of candidates) {
      if (!file.type.startsWith('image/') || file.size > MAX_SIZE_BYTES) {
        skipped++;
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) {
      setNotice('Formato o tamaño no permitido (máx. 5 MB)');
      return;
    }

    if (productoId) {
      setUploading(true);
      try {
        const isFirst = images.length === 0;
        for (let i = 0; i < validFiles.length; i++) {
          await archivosService.subirParaProducto(productoId, validFiles[i], {
            esPrincipal: isFirst && i === 0,
            orden: images.length + i + 1,
          });
        }
        await refetchFromApi();
        if (skipped > 0) setNotice('Algunas imágenes no pudieron agregarse');
      } catch (error) {
        console.error('[CargaImagen] Error al subir imagen:', error);
        const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
        setNotice(`Error: ${errorMsg}`);
      } finally {
        setUploading(false);
      }
    } else {
      const newItems = await Promise.all(
        validFiles.map(async (file, i) => ({
          id: toLocalId(file),
          url: await readAsDataUrl(file),
          file,
          isExisting: false,
          esPrincipal: images.length === 0 && i === 0,
        }))
      );

      setImages(prev => {
        const merged = [...prev];
        for (const item of newItems) {
          if (merged.some(e => e.id === item.id) || merged.length >= MAX_IMAGES) continue;
          merged.push(item);
        }
        return merged;
      });

      if (images.length === 0) setActiveIndex(0);
      if (skipped > 0) setNotice('Algunas imágenes no pudieron agregarse');
    }
  };

  const handleSelectIndex = (idx: number) => {
    if (!isVisible) return;
    const next = images[idx];
    if (!next) return;
    setActiveIndex(idx);
    if (next.file) setMainImageInForm(next.file);
  };

  const handleSetPrincipal = async (idx: number) => {
    const img = images[idx];
    if (!img?.isExisting || img.esPrincipal || actionId) return;

    setActionId(img.id);
    try {
      await archivosService.marcarPrincipal(img.id);
      if (productoId) {
        await refetchFromApi();
      } else {
        setImages(prev => prev.map((item, i) => ({ ...item, esPrincipal: i === idx })));
        setActiveIndex(idx);
      }
      setNotice('Imagen principal actualizada');
    } catch (error) {
      console.error('[CargaImagen] Error al actualizar imagen principal:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      setNotice(`Error: ${errorMsg}`);
    } finally {
      setActionId(null);
    }
  };

  const handleRemoveAt = async (idx: number) => {
    if (!isVisible || actionId) return;
    const img = images[idx];
    if (!img) return;

    if (img.isExisting) {
      setActionId(img.id);
      try {
        await archivosService.eliminar(img.id);
        if (productoId) {
          await refetchFromApi();
        } else {
          setImages(prev => {
            const next = prev.filter((_, i) => i !== idx);
            setActiveIndex(Math.min(activeIndex, Math.max(0, next.length - 1)));
            return next;
          });
        }
      } catch (error) {
        console.error('[CargaImagen] Error al eliminar imagen:', error);
        const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
        setNotice(`Error: ${errorMsg}`);
      } finally {
        setActionId(null);
      }
    } else {
      setImages(prev => {
        const next = prev.filter((_, i) => i !== idx);
        const nextActive = Math.min(
          idx === activeIndex ? activeIndex : idx < activeIndex ? Math.max(0, activeIndex - 1) : activeIndex,
          Math.max(0, next.length - 1)
        );
        setActiveIndex(nextActive);
        if (next.length === 0) setMainImageInForm(undefined);
        else if (next[nextActive]?.file) setMainImageInForm(next[nextActive].file);
        return next;
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-700">
          Imagen
          {isFieldRequired('imagen') && <span className="text-red-500 ml-1">*</span>}
        </label>
        {showCheck && renderCheck?.()}
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50/40 p-2.5">
        <div className="group relative aspect-square w-full max-h-[260px] overflow-hidden rounded-lg border border-gray-200 bg-white">
          {uploading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
              <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
            </div>
          )}

          {activeImage?.url ? (
            <>
              <img
                src={activeImage.url}
                alt="Preview"
                className="h-full w-full object-cover transition-transform duration-200 ease-out group-hover:scale-[1.04]"
              />

              {activeImage.esPrincipal && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/90 text-white text-[10px] font-semibold shadow-sm pointer-events-none">
                  <Star className="w-3 h-3 fill-white" />
                  Principal
                </span>
              )}

              <button
                type="button"
                onClick={() => void handleRemoveAt(activeIndex)}
                disabled={!!actionId || uploading}
                className="absolute top-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/90 border border-gray-200 text-gray-700 shadow-sm hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors disabled:opacity-40"
                aria-label="Quitar imagen"
              >
                {actionId === activeImage.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </button>

              {activeImage.isExisting && !activeImage.esPrincipal && (
                <button
                  type="button"
                  onClick={() => void handleSetPrincipal(activeIndex)}
                  disabled={!!actionId || uploading}
                  className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 border border-gray-200 text-gray-600 text-[11px] font-medium shadow-sm hover:bg-amber-50 hover:border-amber-300 hover:text-amber-600 transition-colors disabled:opacity-40"
                  aria-label="Marcar como imagen principal"
                >
                  <Star className="w-3 h-3" />
                  Marcar principal
                </button>
              )}
            </>
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-xs font-medium text-gray-600">Sin imagen</p>
                <p className="text-[11px] text-gray-500">Sube una foto para identificarlo rápido</p>
              </div>
            </div>
          )}
        </div>

        {images.length > 1 && (
          <div className="mt-2 flex items-center gap-2.5 overflow-x-auto pb-1">
            {images.map((img, idx) => {
              const isActive = idx === activeIndex;
              const isActioning = actionId === img.id;
              return (
                <div key={img.id} className="relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleSelectIndex(idx)}
                    disabled={isActioning}
                    className={`relative h-12 w-12 overflow-hidden rounded-md border bg-white transition-colors ${
                      isActive
                        ? 'border-violet-400 ring-2 ring-violet-200'
                        : 'border-gray-200 hover:border-gray-300'
                    } disabled:opacity-50`}
                    aria-label={`Imagen ${idx + 1}`}
                  >
                    <img src={img.url} alt="" className="h-full w-full object-cover" />
                    {isActioning && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <Loader2 className="w-4 h-4 text-violet-500 animate-spin" />
                      </div>
                    )}
                  </button>

                  {img.esPrincipal && (
                    <span
                      className="absolute -top-1.5 -left-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 shadow"
                      title="Imagen principal"
                    >
                      <Star className="w-2.5 h-2.5 fill-white text-white" />
                    </span>
                  )}

                  {!img.esPrincipal && img.isExisting && !isActioning && (
                    <button
                      type="button"
                      onClick={() => void handleSetPrincipal(idx)}
                      disabled={!!actionId}
                      className="absolute -top-1.5 -left-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-amber-50 hover:border-amber-300 disabled:opacity-40"
                      title="Marcar como principal"
                      aria-label="Marcar como principal"
                    >
                      <Star className="w-2.5 h-2.5 text-gray-300 hover:text-amber-400" />
                    </button>
                  )}

                  {!isActioning && (
                    <button
                      type="button"
                      onClick={() => void handleRemoveAt(idx)}
                      disabled={!!actionId}
                      className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 shadow hover:bg-red-50 hover:border-red-200 hover:text-red-500 disabled:opacity-40"
                      aria-label={`Quitar imagen ${idx + 1}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-2.5 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] text-gray-500">PNG, JPG, GIF hasta 5 MB · Máx. {MAX_IMAGES} imágenes</p>
            {notice && <p className="text-[11px] text-amber-600 mt-0.5">{notice}</p>}
          </div>
          <div className="flex-shrink-0">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              disabled={!canAddMore || uploading}
              onChange={handleAddImages}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className={`inline-flex items-center gap-2 px-3 h-9 border border-gray-300 rounded-md text-xs font-medium bg-white transition-colors ${
                canAddMore && !uploading
                  ? 'cursor-pointer text-gray-700 hover:bg-gray-50'
                  : 'cursor-not-allowed text-gray-400 bg-gray-50'
              }`}
            >
              {uploading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ImageIcon className="w-3.5 h-3.5" />
              )}
              {images.length === 0 ? 'Subir imagen' : 'Agregar'}
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};


